<template>
  <div class="landing-call-settings space-y-6">
    <div v-if="loadState === 'loading'" class="text-body" style="color: var(--text-secondary);">
      Loading call settings…
    </div>
    <div v-else-if="loadState === 'unavailable'" class="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" role="alert">
      Call settings are temporarily unavailable. Your leads and existing numbers are unaffected; try again in a moment.
      <button type="button" class="underline ml-1" @click="load()">
        Try again
      </button>
    </div>
    <div v-else-if="loadState === 'error'" class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
      Couldn't load call settings.
      <button type="button" class="underline ml-1" @click="load()">
        Try again
      </button>
    </div>

    <template v-else-if="settings">
      <!-- Business notification recipients -->
      <section class="card p-5 space-y-3" aria-labelledby="lead-recipients-title">
        <h2 id="lead-recipients-title" class="text-lg font-semibold" style="color: var(--text-primary);">
          Who hears about new leads
        </h2>
        <label class="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-test="notifications-enabled"
            :checked="settings.notifications && settings.notifications.enabled"
            :disabled="busy.notifications || undefined"
            @change="toggleNotifications($event.target.checked)">
          <span>Send a notification for every new lead</span>
        </label>
        <ul v-if="activeRecipients.length" class="divide-y text-sm">
          <li v-for="recipient in activeRecipients" :key="recipient.id" class="py-2 flex flex-wrap items-center justify-between gap-2">
            <span class="break-all">{{ recipient.email }}</span>
            <span class="flex items-center gap-2">
              <span
                class="text-xs rounded-full px-2 py-0.5"
                :class="recipient.verified ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'">
                {{ recipient.verified ? 'Verified' : 'Awaiting verification' }}
              </span>
              <button
                v-if="!recipient.verified"
                type="button"
                class="btn btn-secondary text-xs"
                :disabled="busy[`recipient-${recipient.id}`] || undefined"
                @click="resendRecipient(recipient)">
                Resend
              </button>
              <button
                type="button"
                class="btn btn-secondary text-xs"
                :aria-label="`Remove ${recipient.email}`"
                :disabled="busy[`recipient-${recipient.id}`] || undefined"
                @click="revokeRecipient(recipient)">
                Remove
              </button>
            </span>
          </li>
        </ul>
        <p v-else class="text-sm" style="color: var(--text-secondary);">
          No notification recipients yet. Add the addresses that should hear about new leads; each one confirms by email first.
        </p>
        <form class="flex flex-wrap gap-2" data-test="recipient-form" @submit.prevent="addRecipient">
          <label class="sr-only" for="landing-recipient-email">Recipient email</label>
          <input
            id="landing-recipient-email"
            v-model="recipientEmail"
            data-test="recipient-email"
            type="email"
            required
            autocomplete="email"
            class="form-input flex-1 min-w-0"
            placeholder="frontdesk@yourbusiness.com">
          <button type="submit" class="btn btn-primary" :disabled="busy.recipient || undefined">
            {{ busy.recipient ? 'Adding…' : 'Add recipient' }}
          </button>
        </form>
        <p role="status" aria-live="polite" class="text-sm" :class="toneClass(messages.recipients)">
          {{ messages.recipients?.text || '' }}
        </p>
      </section>

      <!-- Call tracking provider -->
      <section class="card p-5 space-y-4" aria-labelledby="call-tracking-title">
        <h2 id="call-tracking-title" class="text-lg font-semibold" style="color: var(--text-primary);">
          Call tracking
        </h2>
        <div v-if="!settings.voice_enabled" class="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
          Call tracking is temporarily unavailable. Existing leads are safe; setup changes will work again shortly.
        </div>

        <fieldset v-if="!settings.connections.length" class="space-y-2" :disabled="!settings.voice_enabled || undefined">
          <legend class="text-sm font-medium mb-1">
            How should calls be tracked?
          </legend>
          <label class="flex items-start gap-2 text-sm">
            <input v-model="provider" type="radio" value="builtin" data-test="provider-builtin" class="mt-1">
            <span><strong>Built-in tracking numbers</strong> — Made.ly provides local numbers that forward to your business line (US numbers only).</span>
          </label>
          <label class="flex items-start gap-2 text-sm">
            <input v-model="provider" type="radio" value="callrail" data-test="provider-callrail" class="mt-1">
            <span><strong>CallRail</strong> — keep your existing CallRail account and tracking numbers.</span>
          </label>

          <div v-if="provider === 'builtin'" class="pt-2">
            <button type="button" class="btn btn-primary" data-test="builtin-setup" :disabled="busy.builtin || undefined" @click="setupBuiltin">
              {{ busy.builtin ? 'Starting…' : 'Set up built-in numbers' }}
            </button>
          </div>

          <form v-if="provider === 'callrail'" class="pt-2 space-y-2" data-test="callrail-form" @submit.prevent="connectCallRail">
            <label class="flex flex-col text-sm">
              <span class="font-medium">CallRail API key</span>
              <input
                v-model="apiKey"
                data-test="callrail-api-key"
                type="password"
                autocomplete="off"
                spellcheck="false"
                required
                class="form-input mt-1">
              <span class="text-xs mt-1" style="color: var(--text-secondary);">We store the key encrypted and never show it again.</span>
            </label>
            <label class="flex flex-col text-sm">
              <span class="font-medium">Landing site</span>
              <select v-model="callrailSite" data-test="callrail-site" class="form-select mt-1">
                <option value="">Choose later</option>
                <option v-for="site in settings.sites" :key="site.id" :value="site.id">{{ site.name }}</option>
              </select>
            </label>
            <button type="submit" class="btn btn-primary" :disabled="busy.callrail || undefined">
              {{ busy.callrail ? 'Connecting…' : 'Connect CallRail' }}
            </button>
          </form>
        </fieldset>

        <p role="status" aria-live="polite" class="text-sm" :class="toneClass(messages.provider)">
          {{ messages.provider?.text || '' }}
        </p>

        <article
          v-for="connection in settings.connections"
          :key="connection.id"
          class="rounded-lg border p-4 space-y-3"
          :data-test="`connection-${connection.id}`"
          :aria-label="connection.provider === 'callrail' ? 'CallRail connection' : 'Built-in numbers'">
          <header class="flex flex-wrap items-center justify-between gap-2">
            <h3 class="font-semibold">
              {{ connection.provider === 'callrail' ? 'CallRail' : 'Built-in numbers' }}
            </h3>
            <span class="text-xs rounded-full bg-gray-100 text-gray-800 px-2 py-0.5">{{ connectionStateLabel(connection) }}</span>
          </header>

          <!-- Two-phase CallRail: credential only, then explicit selection -->
          <template v-if="connection.provider === 'callrail' && connection.selection_state !== 'selected'">
            <p v-if="connection.selection_state === 'enumerating'" class="text-sm">
              Reading your CallRail companies… This usually takes under a minute.
              <button type="button" class="underline" @click="refreshConnection(connection)">
                Check again
              </button>
            </p>
            <p v-else-if="connection.selection_state === 'enumeration_failed'" class="text-sm text-red-700" role="alert">
              We couldn't read your CallRail account{{ enumerationReason(connection) }}. Check the API key, then dismiss this and connect again.
            </p>
            <form
              v-else-if="connection.selection_state === 'selection_required'"
              class="grid gap-2 sm:grid-cols-3"
              :data-test="`selection-form-${connection.id}`"
              @submit.prevent="selectTracker(connection)">
              <label class="flex flex-col text-sm">
                <span class="font-medium">Company</span>
                <select v-model="selection(connection.id).company_id" class="form-select mt-1" :data-test="`company-${connection.id}`" required>
                  <option value="" disabled>Choose a company</option>
                  <option v-for="company in companiesFor(connection)" :key="company.id" :value="company.id">{{ company.name || company.id }}</option>
                </select>
              </label>
              <label class="flex flex-col text-sm">
                <span class="font-medium">Tracker</span>
                <select v-model="selection(connection.id).tracker_id" class="form-select mt-1" :data-test="`tracker-${connection.id}`" required>
                  <option value="" disabled>Choose a tracker</option>
                  <option v-for="tracker in trackersFor(connection)" :key="tracker.id" :value="tracker.id">{{ tracker.name || tracker.id }}</option>
                </select>
              </label>
              <label class="flex flex-col text-sm">
                <span class="font-medium">Landing site</span>
                <select v-model="selection(connection.id).website_id" class="form-select mt-1" :data-test="`select-site-${connection.id}`">
                  <option value="">Site chosen at connect</option>
                  <option v-for="site in settings.sites" :key="site.id" :value="site.id">{{ site.name }}</option>
                </select>
              </label>
              <div class="sm:col-span-3">
                <button type="submit" class="btn btn-primary" :disabled="busy[`select-${connection.id}`] || undefined">
                  {{ busy[`select-${connection.id}`] ? 'Saving…' : 'Use this tracker' }}
                </button>
              </div>
            </form>
            <button type="button" class="btn btn-secondary text-xs" :disabled="busy[`dismiss-${connection.id}`] || undefined" @click="dismiss(connection)">
              Dismiss this connection
            </button>
          </template>

          <!-- Built-in destination: US numbers, verified before forwarding -->
          <template v-if="connection.provider === 'builtin'">
            <p class="text-sm">
              <span v-if="connection.active_destination">Calls forward to {{ connection.active_destination }}.</span>
              <span v-else>No forwarding number yet.</span>
              <span v-if="connection.routing_state === 'pending' && connection.requested_destination">
                Switching forwarding to {{ connection.requested_destination }}…
              </span>
            </p>
            <form class="flex flex-wrap items-end gap-2" :data-test="`destination-form-${connection.id}`" @submit.prevent="verifyDestination(connection)">
              <label class="flex flex-col text-sm w-full sm:w-auto sm:flex-1 min-w-0">
                <span class="font-medium">Forward calls to</span>
                <input
                  v-model="destinations[connection.id]"
                  type="tel"
                  autocomplete="tel"
                  required
                  class="form-input mt-1"
                  :data-test="`destination-${connection.id}`"
                  :aria-describedby="`destination-hint-${connection.id}`">
                <span :id="`destination-hint-${connection.id}`" class="text-xs mt-1" style="color: var(--text-secondary);">
                  US numbers only. We call it once with a code to prove you control it.
                </span>
              </label>
              <button type="submit" class="btn btn-primary" :disabled="busy[`verify-${connection.id}`] || !settings.voice_enabled || undefined">
                Verify number
              </button>
            </form>
            <div v-if="verifications[connection.id]" class="text-sm rounded p-3" style="background: var(--bg-surface-sunken, #f9fafb);" role="status" aria-live="polite">
              <p>{{ verifications[connection.id].text }}</p>
              <p v-if="verifications[connection.id].code" class="mt-1">
                Enter code <strong class="tracking-widest">{{ verifications[connection.id].code }}</strong> when the verification call asks for it.
              </p>
              <button
                v-if="verifications[connection.id].verified"
                type="button"
                class="btn btn-primary text-xs mt-2"
                :disabled="busy[`route-${connection.id}`] || undefined"
                @click="applyDestination(connection)">
                Forward calls to this number
              </button>
            </div>
          </template>

          <!-- Manual CallRail webhook setup after selection -->
          <div v-if="webhooks[connection.id]" class="text-sm rounded p-3 space-y-1" style="background: var(--bg-surface-sunken, #f9fafb);" :data-test="`webhook-${connection.id}`">
            <p class="font-medium">
              Add this webhook in CallRail
            </p>
            <p>In CallRail, open Integrations → Webhooks and add this exact URL to each event below. Keep any webhooks you already use.</p>
            <p class="break-all">
              <code>{{ webhooks[connection.id].callback_url }}</code>
            </p>
            <ul class="list-disc pl-5">
              <li v-for="event in webhooks[connection.id].required_events || []" :key="event">
                <code>{{ event }}</code>
              </li>
            </ul>
            <p v-if="webhooks[connection.id].action === 'none'" class="text-green-700">
              Webhook verified.
            </p>
          </div>

          <!-- Provider health: read-only capability document -->
          <div v-if="connection.selection_state === 'selected' || connection.provider === 'builtin'" class="space-y-2 text-sm">
            <div v-if="capabilityState[connection.id] === 'loading'" style="color: var(--text-secondary);">
              Checking provider health…
            </div>
            <div v-else-if="capabilityState[connection.id] === 'unavailable'" class="text-amber-800">
              Provider health is unavailable right now.
            </div>
            <template v-else-if="caps(connection)">
              <ul class="space-y-1">
                <li>{{ caps(connection).telephony_ready ? 'Calls can be forwarded' : 'Call forwarding not yet proven' }}</li>
                <li>{{ caps(connection).attribution_ready ? 'Attribution proven' : 'Attribution not proven' }}</li>
                <li v-if="caps(connection).fields && caps(connection).fields.connected_seconds === false">
                  CallRail does not report how long a call was connected.
                </li>
                <li v-if="caps(connection).pools && caps(connection).pools.capacity !== null && caps(connection).pools.capacity !== undefined">
                  {{ caps(connection).pools.available }} of {{ caps(connection).pools.capacity }} tracking numbers available
                </li>
              </ul>
              <details>
                <summary class="cursor-pointer">
                  Health checks
                </summary>
                <ul class="mt-1 space-y-1">
                  <li v-for="(check, index) in caps(connection).checks || []" :key="`${check.key}-${index}`">
                    {{ humanize(check.key) }}: {{ humanize(check.state) }}<span v-if="check.code"> ({{ humanize(check.code) }})</span>
                  </li>
                </ul>
              </details>
              <ul v-if="finalizationNotes(connection).length" class="text-amber-800 space-y-1">
                <li v-for="note in finalizationNotes(connection)" :key="note">
                  {{ note }}
                </li>
              </ul>
              <button type="button" class="btn btn-secondary text-xs" :disabled="busy[`refresh-${connection.id}`] || !settings.voice_enabled || undefined" @click="checkSetup(connection)">
                {{ connection.provider === 'callrail' ? 'Check webhook setup' : 'Refresh health' }}
              </button>

              <!-- Explicit CallRail exporter cutover -->
              <div v-if="connection.provider === 'callrail'" class="rounded border p-3 space-y-2" :data-test="`exporter-${connection.id}`">
                <p class="font-medium">
                  Conversion exporter
                </p>
                <p>{{ exporterLabel(caps(connection).export_ownership) }}</p>
                <p class="text-xs" style="color: var(--text-secondary);">
                  Made.ly does not send call conversions to ad platforms. Turning export off records that CallRail no longer does either.
                </p>
                <label class="flex items-start gap-2">
                  <input v-model="cutoverTarget(connection.id).confirmed" type="checkbox" class="mt-1" :data-test="`cutover-confirm-${connection.id}`">
                  <span>I confirm CallRail no longer sends these calls to an ad platform as conversions.</span>
                </label>
                <button
                  type="button"
                  class="btn btn-secondary text-xs"
                  :data-test="`cutover-${connection.id}`"
                  :disabled="!cutoverTarget(connection.id).confirmed || busy[`cutover-${connection.id}`] || undefined"
                  @click="cutover(connection)">
                  Turn off call conversion export
                </button>
              </div>
            </template>
          </div>
          <p role="status" aria-live="polite" class="text-sm" :class="toneClass(messages[connection.id])">
            {{ messages[connection.id]?.text || '' }}
          </p>
        </article>
      </section>

      <!-- Business-wide qualification threshold -->
      <section class="card p-5 space-y-3" aria-labelledby="call-threshold-title">
        <h2 id="call-threshold-title" class="text-lg font-semibold" style="color: var(--text-primary);">
          Qualified call length
        </h2>
        <form class="flex flex-wrap items-end gap-2" data-test="threshold-form" @submit.prevent="saveThreshold">
          <label class="flex flex-col text-sm">
            <span class="font-medium">Count a call as qualified after it has been connected for</span>
            <span class="flex items-center gap-2 mt-1">
              <input
                v-model.number="thresholdDraft"
                data-test="threshold-seconds"
                type="number"
                min="1"
                max="86400"
                step="1"
                required
                class="form-input w-28">
              <span>seconds</span>
            </span>
          </label>
          <button type="submit" class="btn btn-primary" data-test="threshold-save" :disabled="busy.threshold || undefined">
            {{ busy.threshold ? 'Saving…' : 'Save' }}
          </button>
        </form>
        <p class="text-xs" style="color: var(--text-secondary);">
          Applies to calls that start after you save. Calls without a reported connected duration are never counted as qualified by length.
        </p>
        <p role="status" aria-live="polite" class="text-sm" :class="toneClass(messages.threshold)">
          {{ messages.threshold?.text || '' }}
        </p>
      </section>
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { apiRequest, humanize, outcomeUnknown, versionTag } from './native_api'

const props = defineProps({
  workspaceId: { type: String, required: true },
  apiBase: { type: String, default: null },
  operationsBase: { type: String, default: null },
  csrfToken: { type: String, default: '' },
  initialSettings: { type: Object, default: null },
  pollIntervalMs: { type: Number, default: 2000 },
  pollAttempts: { type: Number, default: 150 }
})

const EXPORTER_LABELS = {
  cutover_pending: 'Cutover pending. No call is exported until the cutover is verified.',
  disabled: 'Call conversion export is off.',
  observing_callrail: 'CallRail exports call conversions.',
  none: 'No conversion exporter confirmed yet.'
}

const base = computed(() => `${props.apiBase || `/ajax/landing_workspaces/${props.workspaceId}`}/call_settings`)
const operationsBase = computed(() => props.operationsBase || `/ajax/landing_pages/workspaces/${props.workspaceId}/operations`)
const settings = ref(null)
const loadState = ref('loading')
const busy = reactive({})
const messages = reactive({})
const capabilities = reactive({})
const capabilityState = reactive({})
const selections = reactive({})
const cutovers = reactive({})
const destinations = reactive({})
const verifications = reactive({})
const webhooks = reactive({})
const provider = ref('')
const apiKey = ref('')
const callrailSite = ref('')
const recipientEmail = ref('')
const thresholdDraft = ref(60)
let disposed = false

const activeRecipients = computed(() => (settings.value?.recipients || []).filter((recipient) => !recipient.revoked))

function toneClass(message) {
  if (!message) return ''
  return { success: 'text-green-700', conflict: 'text-amber-800', error: 'text-red-700', info: '' }[message.kind] || ''
}

function say(key, kind, text) {
  messages[key] = { kind, text }
}

function sleep(ms) {
  return new Promise((resolve) => { setTimeout(resolve, ms) })
}

function apply(data) {
  settings.value = {
    voice_enabled: Boolean(data.voice_enabled),
    notifications: data.notifications || { enabled: true, version: 1 },
    recipients: data.recipients || [],
    threshold: data.threshold || {},
    connections: data.connections || [],
    sites: data.sites || []
  }
  thresholdDraft.value = settings.value.threshold.connected_call_threshold_seconds ?? 60
  settings.value.connections.forEach((connection) => loadCapabilities(connection))
}

async function load() {
  const result = await apiRequest(base.value)
  if (!result.ok) {
    if (!settings.value) loadState.value = result.status === 503 ? 'unavailable' : 'error'
    return false
  }
  apply(result.data || {})
  loadState.value = 'ready'
  return true
}

async function loadCapabilities(connection) {
  capabilityState[connection.id] = 'loading'
  const result = await apiRequest(`${base.value}/connections/${connection.id}/capabilities`)
  if (!result.ok) {
    capabilityState[connection.id] = 'unavailable'
    return
  }
  capabilities[connection.id] = result.data
  capabilityState[connection.id] = 'ready'
}

function caps(connection) {
  return capabilities[connection.id] || null
}

// Bounded operation poll. `onTick` runs before each read (e.g. to fetch a
// verification code that only exists once the worker has dispatched).
async function pollOperation(id, onTick = null) {
  for (let attempt = 0; attempt < props.pollAttempts && !disposed; attempt += 1) {
    if (attempt > 0) await sleep(props.pollIntervalMs)
    if (disposed) return null
    if (onTick) await onTick()
    const result = await apiRequest(`${operationsBase.value}/${id}`)
    if (result.ok && result.data && ['succeeded', 'failed'].includes(result.data.state)) return result.data
    if (!result.ok && result.status !== 503) return null
  }
  return null
}

function voiceError(result, fallback) {
  if (outcomeUnknown(result)) return "We couldn't confirm whether that change went through, so we reloaded the current settings. Check them before trying again."
  if (result.status === 503) return 'The call provider is temporarily unavailable. Nothing was changed; try again shortly.'
  if (result.status === 412) return 'This connection changed since you loaded it. We reloaded it; review and try again.'
  const code = result.error?.code
  const known = {
    invalid_destination: 'Enter a valid US phone number that can receive calls.',
    forwarding_loop: 'That number is one of your tracking numbers; choose your business line instead.',
    destination_unverified: 'Verify this number before forwarding calls to it.',
    verification_limit_exceeded: 'Too many verification calls this hour. Try again later.',
    customer_confirmation_required: 'Confirm the cutover before recording it.',
    connection_exists: 'That CallRail company is already connected.',
    website_required: 'Choose the landing site this tracker serves.',
    company_change_requires_new_connection: 'A different company needs its own connection.'
  }
  return known[code] || `${fallback} (${humanize(code) || 'request failed'}).`
}

// ---- Recipients ---------------------------------------------------------
async function addRecipient() {
  if (busy.recipient) return
  busy.recipient = true
  messages.recipients = null
  const email = recipientEmail.value.trim()
  const result = await apiRequest(`${base.value}/recipients`, { method: 'POST', body: { email }, csrfToken: props.csrfToken })
  busy.recipient = false
  if (result.ok && result.data) {
    settings.value.recipients = settings.value.recipients.filter((row) => row.id !== result.data.id).concat([result.data])
    recipientEmail.value = ''
    say('recipients', 'success', `We sent a confirmation to ${result.data.email}. Notifications start once it is confirmed.`)
    return
  }
  if (outcomeUnknown(result) || result.status === 409) {
    say('recipients', 'conflict', outcomeUnknown(result) ?
      "We couldn't confirm whether the recipient was added, so we reloaded the list." :
      'That address is already on the list. We reloaded it.')
    await load()
    return
  }
  const text = {
    409: 'That address is already on the list.',
    429: 'A confirmation was sent recently. Check the inbox before sending another.',
    422: result.error?.code === 'recipient_limit' ? 'You can add up to 20 recipients.' : 'Enter a valid email address.'
  }[result.status] || "Couldn't add the recipient. Try again."
  say('recipients', 'error', text)
}

async function resendRecipient(recipient) {
  const key = `recipient-${recipient.id}`
  busy[key] = true
  const result = await apiRequest(`${base.value}/recipients/${recipient.id}/resend`, {
    method: 'POST', body: {}, etag: versionTag(recipient.version), csrfToken: props.csrfToken
  })
  busy[key] = false
  if (result.ok) {
    say('recipients', 'success', `Confirmation sent again to ${recipient.email}.`)
  } else if (result.status === 412) {
    say('recipients', 'conflict', 'This recipient changed since you loaded it. We reloaded the list.')
  } else {
    say('recipients', 'error', result.status === 429 ? 'A confirmation was sent recently. Check the inbox first.' : "Couldn't resend the confirmation.")
  }
  await load()
}

async function revokeRecipient(recipient) {
  const key = `recipient-${recipient.id}`
  busy[key] = true
  const result = await apiRequest(`${base.value}/recipients/${recipient.id}`, {
    method: 'DELETE', body: {}, etag: versionTag(recipient.version), csrfToken: props.csrfToken
  })
  busy[key] = false
  if (result.ok) say('recipients', 'success', `${recipient.email} will no longer receive lead notifications.`)
  else if (result.status === 412) say('recipients', 'conflict', 'This recipient changed since you loaded it. We reloaded the list.')
  else say('recipients', 'error', `Couldn't remove ${recipient.email}.`)
  await load()
}

async function toggleNotifications(enabled) {
  busy.notifications = true
  const result = await apiRequest(`${base.value}/notifications`, {
    method: 'PATCH', body: { enabled }, etag: versionTag(settings.value.notifications.version), csrfToken: props.csrfToken
  })
  busy.notifications = false
  if (result.ok && result.data) {
    settings.value.notifications = result.data
    say('recipients', 'success', enabled ? 'Lead notifications are on.' : 'Lead notifications are off.')
    return
  }
  say('recipients', result.status === 412 ? 'conflict' : 'error', result.status === 412 ? 'Notification settings changed since you loaded them. We reloaded them.' : "Couldn't change notifications.")
  await load()
}

// ---- Provider setup -----------------------------------------------------
async function setupBuiltin() {
  busy.builtin = true
  messages.provider = null
  const result = await apiRequest(`${base.value}/connections`, { method: 'POST', body: { provider: 'builtin' }, csrfToken: props.csrfToken })
  if (!result.ok) {
    busy.builtin = false
    say('provider', 'error', voiceError(result, "Couldn't start built-in numbers"))
    await load()
    return
  }
  say('provider', 'info', 'Setting up your tracking numbers…')
  const operation = await pollOperation(result.data.id)
  busy.builtin = false
  if (operation?.state === 'succeeded') say('provider', 'success', 'Built-in numbers are ready. Add the number calls should forward to.')
  else if (operation?.state === 'failed') say('provider', 'error', `Setup failed (${humanize(operation.error?.code)}).`)
  else say('provider', 'info', 'Setup is still running. Refresh this page in a minute.')
  await load()
}

async function connectCallRail() {
  if (busy.callrail) return
  busy.callrail = true
  messages.provider = null
  const body = { provider: 'callrail', api_key: apiKey.value }
  if (callrailSite.value) body.website_id = callrailSite.value
  // The key is write-only: it leaves the page with this one request.
  apiKey.value = ''
  const result = await apiRequest(`${base.value}/connections`, { method: 'POST', body, csrfToken: props.csrfToken })
  busy.callrail = false
  if (!result.ok) {
    say('provider', 'error', voiceError(result, "Couldn't connect CallRail"))
    await load()
    return
  }
  say('provider', 'success', 'CallRail key saved. Reading your companies and trackers.')
  await load()
}

async function refreshConnection(connection) {
  await load()
  await loadCapabilities(connection)
}

function selection(id) {
  if (!selections[id]) selections[id] = { company_id: '', tracker_id: '', website_id: '' }
  return selections[id]
}

function cutoverTarget(id) {
  if (!cutovers[id]) cutovers[id] = { confirmed: false }
  return cutovers[id]
}

function companiesFor(connection) {
  return caps(connection)?.companies || []
}

function trackersFor(connection) {
  const chosen = companiesFor(connection).find((company) => company.id === selection(connection.id).company_id)
  return chosen ? chosen.trackers || [] : []
}

function enumerationReason(connection) {
  const reason = caps(connection)?.enumeration?.reason
  return reason ? ` (${humanize(reason)})` : ''
}

async function selectTracker(connection) {
  const key = `select-${connection.id}`
  busy[key] = true
  messages[connection.id] = null
  const chosen = selection(connection.id)
  const company = companiesFor(connection).find((row) => row.id === chosen.company_id)
  const body = { company_id: chosen.company_id, tracker_id: chosen.tracker_id }
  if (company?.account_id) body.account_id = company.account_id
  if (chosen.website_id) body.website_id = chosen.website_id
  const result = await apiRequest(`${base.value}/connections/${connection.id}`, {
    method: 'PATCH', body, etag: versionTag(connection.version), csrfToken: props.csrfToken
  })
  if (!result.ok) {
    busy[key] = false
    say(connection.id, result.status === 412 ? 'conflict' : 'error', voiceError(result, "Couldn't select the tracker"))
    await load()
    return
  }
  say(connection.id, 'info', 'Tracker selected. Checking CallRail…')
  if (result.data.operation_id) await showOperationSetup(connection.id, result.data.operation_id)
  busy[key] = false
  await load()
}

async function showOperationSetup(connectionId, operationId) {
  const operation = await pollOperation(operationId)
  if (operation?.state === 'succeeded' && operation.result) {
    webhooks[connectionId] = operation.result
    say(connectionId, operation.result.action === 'none' ? 'success' : 'info', operation.result.action === 'none' ? 'CallRail is verified.' : 'Add the webhook below in CallRail, then check the setup.')
  } else if (operation?.state === 'failed') {
    say(connectionId, 'error', `CallRail check failed (${humanize(operation.error?.code)}).`)
  } else {
    say(connectionId, 'info', 'The CallRail check is still running. Check again in a minute.')
  }
}

async function dismiss(connection) {
  const key = `dismiss-${connection.id}`
  busy[key] = true
  const result = await apiRequest(`${base.value}/connections/${connection.id}`, {
    method: 'PATCH', body: { dismissed: true }, etag: versionTag(connection.version), csrfToken: props.csrfToken
  })
  busy[key] = false
  if (!result.ok) say('provider', 'error', voiceError(result, "Couldn't dismiss the connection"))
  else say('provider', 'success', 'Pending CallRail connection dismissed.')
  await load()
}

async function checkSetup(connection) {
  const key = `refresh-${connection.id}`
  busy[key] = true
  const result = await apiRequest(`${base.value}/connections/${connection.id}/verify`, {
    method: 'POST', body: {}, etag: versionTag(connection.version), csrfToken: props.csrfToken
  })
  if (!result.ok) {
    busy[key] = false
    say(connection.id, result.status === 412 ? 'conflict' : 'error', voiceError(result, "Couldn't check the setup"))
    await load()
    return
  }
  await showOperationSetup(connection.id, result.data.id)
  busy[key] = false
  await refreshConnection(connection)
}

// ---- Built-in destination ---------------------------------------------
async function verifyDestination(connection) {
  const key = `verify-${connection.id}`
  busy[key] = true
  const number = (destinations[connection.id] || '').trim()
  const result = await apiRequest(`${base.value}/connections/${connection.id}/verify`, {
    method: 'POST', body: { destination: number }, etag: versionTag(connection.version), csrfToken: props.csrfToken
  })
  if (!result.ok) {
    busy[key] = false
    verifications[connection.id] = null
    say(connection.id, result.status === 412 ? 'conflict' : 'error', voiceError(result, "Couldn't start the verification call"))
    if ([0, 409, 412].includes(result.status)) await load()
    return
  }
  const operationId = result.data.id
  verifications[connection.id] = { text: `We're placing a verification call to ${number}. Answer it and enter the code shown here.`, number, operationId }
  // The code exists only after the voice worker dispatches the call; until
  // then the challenge answers `verification_unavailable`, which means "not yet".
  const readCode = async() => {
    const current = verifications[connection.id]
    if (!current || current.code) return
    const challenge = await apiRequest(`${base.value}/connections/${connection.id}/verifications/${operationId}`)
    if (challenge.ok && challenge.data?.code && verifications[connection.id]) verifications[connection.id].code = challenge.data.code
  }
  const operation = await pollOperation(operationId, readCode)
  busy[key] = false
  if (!verifications[connection.id]) return
  if (operation?.state === 'succeeded') {
    Object.assign(verifications[connection.id], { text: `${number} is verified.`, verified: true, code: null })
  } else if (operation?.state === 'failed') {
    Object.assign(verifications[connection.id], { text: `Verification did not complete (${humanize(operation.error?.code)}). Try again.`, code: null })
  } else {
    Object.assign(verifications[connection.id], { text: 'The verification is still running. Refresh this page to see the result.', code: null })
  }
  await load()
}

async function applyDestination(connection) {
  const key = `route-${connection.id}`
  busy[key] = true
  const current = settings.value.connections.find((row) => row.id === connection.id) || connection
  const result = await apiRequest(`${base.value}/connections/${connection.id}`, {
    method: 'PATCH', body: { destination: verifications[connection.id].number }, etag: versionTag(current.version), csrfToken: props.csrfToken
  })
  busy[key] = false
  if (result.ok) {
    verifications[connection.id] = null
    say(connection.id, 'success', 'Forwarding change requested. It takes effect once the provider confirms it.')
  } else {
    say(connection.id, result.status === 412 ? 'conflict' : 'error', voiceError(result, "Couldn't change forwarding"))
  }
  await load()
}

// ---- Exporter cutover and threshold ----------------------------------
async function cutover(connection) {
  const key = `cutover-${connection.id}`
  const choice = cutoverTarget(connection.id)
  busy[key] = true
  const result = await apiRequest(`${base.value}/connections/${connection.id}`, {
    method: 'PATCH', body: { export_owner: 'none', customer_confirmed: true }, etag: versionTag(connection.version), csrfToken: props.csrfToken
  })
  busy[key] = false
  if (result.ok) {
    choice.confirmed = false
    say(connection.id, 'success', 'Call conversion export is recorded as off.')
  } else {
    say(connection.id, result.status === 412 ? 'conflict' : 'error', voiceError(result, "Couldn't record the cutover"))
  }
  await refreshConnection(connection)
}

function exporterLabel(ownership) {
  const state = ownership?.state || 'none'
  return EXPORTER_LABELS[state] || humanize(state)
}

function connectionStateLabel(connection) {
  if (connection.provider === 'callrail' && connection.selection_state && connection.selection_state !== 'selected') {
    return humanize(connection.selection_state)
  }
  return humanize(connection.state)
}

function finalizationNotes(connection) {
  const finalization = caps(connection)?.diagnostics?.finalization
  if (!finalization) return []
  const notes = []
  if (finalization.awaiting_provider_read > 0) notes.push(`${finalization.awaiting_provider_read} call(s) waiting for final details from the provider.`)
  Object.entries(finalization.unread_finalized || {}).forEach(([reason, count]) => {
    notes.push(`${count} call(s) recorded without provider details (${humanize(reason)}).`)
  })
  Object.entries(finalization.blocked || {}).forEach(([reason, count]) => {
    notes.push(`${count} call(s) blocked and need attention (${humanize(reason)}).`)
  })
  return notes
}

async function saveThreshold() {
  if (busy.threshold) return
  busy.threshold = true
  messages.threshold = null
  const result = await apiRequest(`${base.value}/threshold`, {
    method: 'PATCH',
    body: { connected_call_threshold_seconds: Number(thresholdDraft.value), qualification_rule_version: settings.value.threshold.qualification_rule_version },
    csrfToken: props.csrfToken
  })
  try {
    if (result.ok && result.data) {
      settings.value.threshold = result.data
      thresholdDraft.value = result.data.connected_call_threshold_seconds
      say('threshold', 'success', 'Threshold saved.')
      return
    }
    if (result.status === 412) {
      say('threshold', 'conflict', 'The threshold was changed by someone else. We reloaded the current setting; review it and save again.')
      await load()
    } else if (outcomeUnknown(result)) {
      say('threshold', 'conflict', "We couldn't confirm the save, so we reloaded the current setting.")
      await load()
    } else if (result.status === 422) {
      say('threshold', 'error', 'Enter a whole number of seconds between 1 and 86,400.')
    } else {
      say('threshold', 'error', "Couldn't save the threshold. Nothing was changed.")
    }
  } finally {
    busy.threshold = false
  }
}

if (props.initialSettings) {
  apply(props.initialSettings)
  loadState.value = 'ready'
}

onMounted(() => {
  if (!props.initialSettings) load()
})

onBeforeUnmount(() => {
  disposed = true
})
</script>
